import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageContent from '../hooks/usePageContent';
import { businessInfo } from '../data';

export default function About() {
  const { page, loading } = usePageContent('om-stausbroe');

  useEffect(() => {
    if (page) {
      document.title = page.title;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute('content', page.meta_description);
      }
    }
  }, [page]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-brand-beige">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-sage border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-brand-beige">
      {/* Page Header */}
      <section className="bg-brand-cream/30 py-12 md:py-16 border-b border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-sage-dark">
            Mød din behandler
          </span>
          <h1 className="mt-2 font-serif text-3xl font-semibold text-brand-neutral-dark md:text-4xl lg:text-5xl">
            {page.hero_title}
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-brand-neutral-dark/80 md:text-base">
            {page.hero_subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-start">
            
            {/* Left Column: Image & RAB Badge */}
            <div className="lg:col-span-5 flex flex-col gap-6 lg:sticky lg:top-24">
              <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-md md:aspect-[16/10] lg:aspect-square">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/d7ec2ecb-d3f6-4a99-ad2f-51a97712ea7d/488642768-1210480340866748-1463153742994692385-n_1.jpg"
                  alt="Anita Zacho - Certificeret Bowen Terapeut"
                  className="h-full w-full object-cover"
                />
              </div>

              {/* RAB Trust Box */}
              <div className="rounded-2xl border border-brand-sage-medium/20 bg-brand-sage-light/40 p-6">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white text-brand-sage shadow-sm">
                    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                    </svg>
                  </div>
                  <div>
                    <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">RAB-Registreret Behandler</h4>
                    <p className="text-xs text-brand-neutral-dark/80 mt-0.5">
                      Sikrer dig professionel standard og etiske retningslinjer.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Text Content */}
            <div className="lg:col-span-7 flex flex-col gap-6">
              <div 
                className="prose prose-stone text-sm leading-relaxed text-brand-neutral-dark/80"
                dangerouslySetInnerHTML={{ __html: page.content }}
              />

              {/* Values list */}
              <div className="mt-6 border-t border-brand-sage-medium/10 pt-8">
                <h3 className="font-serif text-xl font-semibold text-brand-neutral-dark">Mine faglige værdier</h3>
                <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
                  
                  {/* Value 1 */}
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                      <i className="fa-light fa-heart text-sm" />
                    </div>
                    <div>
                      <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">Individuel omsorg</h4>
                      <p className="text-xs text-brand-neutral-dark/80 mt-1">
                        Ingen kroppe er ens. Jeg tilpasser altid behandlingen til din unikke situation og kropshistorik.
                      </p>
                    </div>
                  </div>

                  {/* Value 2 */}
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                      <i className="fa-light fa-graduation-cap text-sm" />
                    </div>
                    <div>
                      <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">Høj faglighed</h4>
                      <p className="text-xs text-brand-neutral-dark/80 mt-1">
                        Gennem min RAB-godkendelse forpligter jeg mig til løbende efteruddannelse for at sikre den bedste kvalitet.
                      </p>
                    </div>
                  </div>

                  {/* Value 3 */}
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                      <i className="fa-light fa-hourglass text-sm" />
                    </div>
                    <div>
                      <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">Tid og ro</h4>
                      <p className="text-xs text-brand-neutral-dark/80 mt-1">
                        Her er der ingen stress eller hurtige konsultationer. Vi tager os den tid, din krop har brug for.
                      </p>
                    </div>
                  </div>

                  {/* Value 4 */}
                  <div className="flex gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-sage-light text-brand-sage">
                      <i className="fa-light fa-leaf text-sm" />
                    </div>
                    <div>
                      <h4 className="font-serif text-sm font-semibold text-brand-neutral-dark">Naturlig balance</h4>
                      <p className="text-xs text-brand-neutral-dark/80 mt-1">
                        Behandlingen understøtter kroppens egne selvregulerende processer uden unødig indgriben.
                      </p>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-brand-cream/30 py-12 border-t border-brand-sage-medium/10">
        <div className="mx-auto max-w-4xl px-6 text-center flex flex-col items-center gap-4">
          <h2 className="font-serif text-2xl font-semibold text-brand-neutral-dark">
            Vil du bestille en tid hos mig?
          </h2>
          <p className="text-sm text-brand-neutral-dark/80 max-w-xl">
            Du kan nemt bestille en tid ved at ringe, sende en SMS eller udfylde kontaktformularen.
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-4">
            <Link
              to="/kontakt"
              className="inline-flex items-center justify-center rounded-full bg-brand-sage px-6 py-2.5 text-sm font-medium text-white shadow-sm transition-all duration-300 hover:bg-brand-sage-dark"
            >
              Gå til kontakt
            </Link>
            <a
              href={`tel:+45${businessInfo.phone.replace(/\s/g, '')}`}
              className="inline-flex items-center justify-center rounded-full border border-brand-sage-medium/30 bg-white/40 px-6 py-2.5 text-sm font-medium text-brand-neutral-dark transition-all duration-300 hover:bg-white/80"
            >
              Ring <span className="whitespace-nowrap ml-1">{businessInfo.phone}</span>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
